import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  CubeIcon,
} from '@heroicons/react/20/solid'
import {
  pdfImportApi,
  productsApi,
  type PDFExtractionResult,
  type PDFExtractedLineItem,
  type ProductSuggestion,
  type AccountSuggestion,
} from '../../lib/api'

type UploadState = 'idle' | 'uploading' | 'processing' | 'preview' | 'creating'

interface EditableLineItem extends PDFExtractedLineItem {
  isEditing: boolean
  selectedProductId: string | null
}

const CONFIDENCE_COLORS = {
  high: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  low: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
}

function getConfidenceLevel(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 80) return 'high'
  if (confidence >= 50) return 'medium'
  return 'low'
}

function formatCurrency(value: number | null, currency = 'EUR'): string {
  if (value === null) return '-'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(value)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleDateString('de-DE')
  } catch {
    return dateStr
  }
}

export function PDFImportPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [state, setState] = useState<UploadState>('idle')
  const [pdfBase64, setPdfBase64] = useState<string | null>(null)
  const [filename, setFilename] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [extractionResult, setExtractionResult] = useState<PDFExtractionResult | null>(null)
  const [lineItems, setLineItems] = useState<EditableLineItem[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [showAccountSearch, setShowAccountSearch] = useState(false)
  const [accountSearchQuery, setAccountSearchQuery] = useState('')
  const [productSearchModal, setProductSearchModal] = useState<{
    lineIndex: number
    query: string
    suggestions: ProductSuggestion[]
    loading: boolean
  } | null>(null)

  // Parse PDF mutation
  const parseMutation = useMutation({
    mutationFn: ({ pdfBase64, filename }: { pdfBase64: string; filename?: string }) =>
      pdfImportApi.parsePDF(pdfBase64, filename),
    onSuccess: (result) => {
      if (result.success) {
        setExtractionResult(result)
        setLineItems(result.extracted.lineItems.map(item => ({
          ...item,
          isEditing: false,
          selectedProductId: item.matchedProductId,
        })))
        setSelectedAccountId(result.extracted.customer.matchedAccountId)
        setState('preview')
      } else {
        setError(result.error || 'Failed to extract data from PDF')
        setState('idle')
      }
    },
    onError: (err: Error) => {
      setError(err.message)
      setState('idle')
    },
  })

  // Create quote mutation
  const createQuoteMutation = useMutation({
    mutationFn: () => {
      if (!extractionResult || !pdfBase64) throw new Error('No extraction data')

      return pdfImportApi.createQuoteFromPDF({
        pdfBase64,
        filename: filename || undefined,
        accountId: selectedAccountId,
        customerName: extractionResult.extracted.customer.name,
        lineItems: lineItems.map(item => ({
          position: item.position,
          articleNumber: item.articleNumber,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          lineTotal: item.lineTotal,
          matchedProductId: item.selectedProductId,
          matchedItemCode: item.matchedItemCode,
          confidence: item.confidence,
          matchType: item.matchType,
        })),
        totals: extractionResult.extracted.totals,
        header: extractionResult.extracted.header,
      })
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['proxy-products'] })
      navigate(`/cpq/quotes/${result.quote.id}`)
    },
    onError: (err: Error) => {
      setError(err.message)
      setState('preview')
    },
  })

  // Account suggestions query
  const { data: accountSuggestions } = useQuery({
    queryKey: ['account-suggestions', extractionResult?.extracted.customer.name, extractionResult?.extracted.customer.address],
    queryFn: () => pdfImportApi.getAccountSuggestions(
      extractionResult?.extracted.customer.name || null,
      extractionResult?.extracted.customer.address || null,
      10
    ),
    enabled: !!extractionResult && showAccountSearch,
  })

  // File drop handler
  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.type === 'application/pdf') {
      handleFileSelect(file)
    } else {
      setError('Please upload a PDF file')
    }
  }, [])

  // File select handler
  const handleFileSelect = (file: File) => {
    setError(null)
    setFilename(file.name)
    setState('uploading')

    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]
      setPdfBase64(base64)
      setState('processing')
      parseMutation.mutate({ pdfBase64: base64, filename: file.name })
    }
    reader.onerror = () => {
      setError('Failed to read file')
      setState('idle')
    }
    reader.readAsDataURL(file)
  }

  // Search products for a line item
  const searchProductsForLine = async (lineIndex: number) => {
    const item = lineItems[lineIndex]
    setProductSearchModal({
      lineIndex,
      query: item.articleNumber || item.description,
      suggestions: [],
      loading: true,
    })

    try {
      const suggestions = await pdfImportApi.getProductSuggestions(
        item.articleNumber,
        item.description,
        10
      )
      setProductSearchModal(prev => prev ? { ...prev, suggestions, loading: false } : null)
    } catch (err) {
      setProductSearchModal(prev => prev ? { ...prev, loading: false } : null)
    }
  }

  // Select product for a line item
  const selectProductForLine = (lineIndex: number, product: ProductSuggestion) => {
    setLineItems(prev => prev.map((item, idx) =>
      idx === lineIndex
        ? {
            ...item,
            selectedProductId: product.id,
            matchedProductId: product.id,
            matchedItemCode: product.itemCode,
            matchedItemName: product.itemName,
            confidence: 100,
            matchType: 'exact_code' as const,
          }
        : item
    ))
    setProductSearchModal(null)
  }

  // Clear product selection
  const clearProductForLine = (lineIndex: number) => {
    setLineItems(prev => prev.map((item, idx) =>
      idx === lineIndex
        ? {
            ...item,
            selectedProductId: null,
            matchedProductId: null,
            matchedItemCode: null,
            matchedItemName: null,
            confidence: 0,
            matchType: 'none' as const,
          }
        : item
    ))
  }

  // Reset to upload state
  const resetUpload = () => {
    setState('idle')
    setPdfBase64(null)
    setFilename(null)
    setError(null)
    setExtractionResult(null)
    setLineItems([])
    setSelectedAccountId(null)
  }

  // Summary calculations
  const matchedCount = lineItems.filter(i => i.selectedProductId).length
  const unmatchedCount = lineItems.length - matchedCount

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Import Purchase Order PDF</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload a customer purchase order PDF to automatically extract and match products
        </p>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XCircleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Upload State */}
      {state === 'idle' && (
        <div
          onDrop={handleFileDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-indigo-500 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-lg font-medium text-gray-900">
            Drop a PDF here, or click to select
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Upload a customer purchase order (Bestellung) to create a quote
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
          />
        </div>
      )}

      {/* Processing State */}
      {(state === 'uploading' || state === 'processing') && (
        <div className="text-center py-12">
          <ArrowPathIcon className="mx-auto h-12 w-12 text-indigo-600 animate-spin" />
          <p className="mt-4 text-lg font-medium text-gray-900">
            {state === 'uploading' ? 'Reading file...' : 'Extracting order data with AI...'}
          </p>
          <p className="mt-2 text-sm text-gray-500">{filename}</p>
        </div>
      )}

      {/* Preview State */}
      {state === 'preview' && extractionResult && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Match Summary */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <CubeIcon className="h-8 w-8 text-indigo-500" />
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Product Matching</p>
                  <p className="text-lg font-semibold">
                    {matchedCount} / {lineItems.length} matched
                  </p>
                  {unmatchedCount > 0 && (
                    <p className="text-xs text-amber-600">
                      {unmatchedCount} items will create proxy products
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Customer */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-8 w-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="text-lg font-semibold truncate max-w-[200px]">
                      {extractionResult.extracted.customer.name || 'Not identified'}
                    </p>
                    {selectedAccountId ? (
                      <p className="text-xs text-green-600">Linked to account</p>
                    ) : (
                      <p className="text-xs text-amber-600">No account linked</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowAccountSearch(!showAccountSearch)}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  {showAccountSearch ? 'Hide' : 'Change'}
                </button>
              </div>

              {/* Account Search */}
              {showAccountSearch && (
                <div className="mt-4 border-t pt-4">
                  <div className="space-y-2">
                    {accountSuggestions?.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => {
                          setSelectedAccountId(account.id)
                          setShowAccountSearch(false)
                        }}
                        className={`w-full text-left px-3 py-2 rounded border ${
                          selectedAccountId === account.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-sm">{account.name}</p>
                        {account.confidence > 0 && (
                          <p className="text-xs text-gray-500">{account.confidence}% match</p>
                        )}
                      </button>
                    ))}
                    {(!accountSuggestions || accountSuggestions.length === 0) && (
                      <p className="text-sm text-gray-500">No matching accounts found</p>
                    )}
                    <button
                      onClick={() => {
                        setSelectedAccountId(null)
                        setShowAccountSearch(false)
                      }}
                      className="w-full text-left px-3 py-2 rounded border border-gray-200 text-sm text-gray-600 hover:border-gray-300"
                    >
                      Continue without linking
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <DocumentTextIcon className="h-8 w-8 text-blue-500" />
                <div className="ml-4">
                  <p className="text-sm text-gray-500">Order Total</p>
                  <p className="text-lg font-semibold">
                    {formatCurrency(
                      extractionResult.extracted.totals.grossTotal ||
                        extractionResult.extracted.totals.netTotal,
                      extractionResult.extracted.totals.currency
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {extractionResult.extracted.lineItems.length} line items
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Header Info */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900">Order Details</h3>
              <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">PO Number</dt>
                  <dd className="text-sm font-medium">{extractionResult.extracted.header.orderNumber || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Order Date</dt>
                  <dd className="text-sm font-medium">{formatDate(extractionResult.extracted.header.orderDate)}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Delivery Date</dt>
                  <dd className="text-sm font-medium">{formatDate(extractionResult.extracted.header.deliveryDate)}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Reference</dt>
                  <dd className="text-sm font-medium">{extractionResult.extracted.header.customerReference || '-'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
              <p className="mt-1 text-sm text-gray-500">
                Review and adjust product matches. Unmatched items will create proxy products for later resolution.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pos
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Article / Description
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matched Product
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lineItems.map((item, idx) => {
                    const confidenceLevel = getConfidenceLevel(item.confidence)
                    const colors = CONFIDENCE_COLORS[confidenceLevel]

                    return (
                      <tr key={idx} className={item.selectedProductId ? '' : 'bg-amber-50'}>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.position}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {item.articleNumber || '-'}
                          </div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {item.description}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {item.quantity} {item.unit || ''}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(item.unitPrice, extractionResult.extracted.totals.currency)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                          {formatCurrency(item.lineTotal, extractionResult.extracted.totals.currency)}
                        </td>
                        <td className="px-4 py-3">
                          {item.selectedProductId ? (
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
                                {item.confidence}%
                              </span>
                              <span className="ml-2 text-sm text-gray-900 truncate max-w-[150px]">
                                {item.matchedItemCode}
                              </span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                              Proxy
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => searchProductsForLine(idx)}
                              className="text-indigo-600 hover:text-indigo-800"
                              title="Search products"
                            >
                              <MagnifyingGlassIcon className="h-5 w-5" />
                            </button>
                            {item.selectedProductId && (
                              <button
                                onClick={() => clearProductForLine(idx)}
                                className="text-red-600 hover:text-red-800"
                                title="Remove match"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <button
              onClick={resetUpload}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel & Start Over
            </button>
            <div className="flex items-center space-x-4">
              {unmatchedCount > 0 && (
                <p className="text-sm text-amber-600">
                  <ExclamationTriangleIcon className="inline h-4 w-4 mr-1" />
                  {unmatchedCount} proxy product(s) will be created
                </p>
              )}
              <button
                onClick={() => {
                  setState('creating')
                  createQuoteMutation.mutate()
                }}
                disabled={createQuoteMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {createQuoteMutation.isPending ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Creating Quote...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Create Quote
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Search Modal */}
      {productSearchModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setProductSearchModal(null)}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Product</h3>
              <p className="text-sm text-gray-500 mb-4">
                Searching for: {lineItems[productSearchModal.lineIndex]?.description}
              </p>

              {productSearchModal.loading ? (
                <div className="text-center py-8">
                  <ArrowPathIcon className="h-8 w-8 text-indigo-600 animate-spin mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">Searching products...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {productSearchModal.suggestions.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => selectProductForLine(productSearchModal.lineIndex, product)}
                      className="w-full text-left px-3 py-2 rounded border border-gray-200 hover:border-indigo-500 hover:bg-indigo-50"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{product.itemCode}</p>
                          <p className="text-sm text-gray-600">{product.itemName}</p>
                          {product.shortDescription && (
                            <p className="text-xs text-gray-400 truncate">{product.shortDescription}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-500">{product.confidence}% match</span>
                          {product.defaultPrice && (
                            <p className="text-sm font-medium">{formatCurrency(parseFloat(product.defaultPrice))}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                  {productSearchModal.suggestions.length === 0 && (
                    <p className="text-center py-8 text-gray-500">No matching products found</p>
                  )}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setProductSearchModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
